/**
 * Almeida's Coffee — homepage interactions.
 * Padrão Dawn: custom elements (HTMLElement + customElements.define),
 * defensivo — cada bloco verifica se os elementos existem antes de agir.
 */
(function () {
  'use strict';

  /* ── Reveal on scroll ────────────────────────────────────────
     Aplica .is-on aos .reveal/.reveal-left/.reveal-right/.reveal-scale
     (CSS já definido em almeidas-motion.css) uma única vez. */
  function initReveal() {
    var els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-on'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-on');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    els.forEach(function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  /* ── almeidas-film ───────────────────────────────────────────
     Banner de vídeo da torra: autoplay muted ao entrar no viewport,
     toca uma vez e fica parado no último frame. A imagem still só
     entra como fallback se o autoplay for bloqueado ou o vídeo falhar. */
  if (!customElements.get('almeidas-film')) {
    customElements.define(
      'almeidas-film',
      class AlmeidasFilm extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('.film-bg');
          if (!this.video || !('IntersectionObserver' in window)) return;

          this.played = false;
          this.io = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.played) {
                  this.played = true;
                  this.classList.add('is-on');
                  // Só cai para a imagem still se o vídeo não conseguir tocar.
                  // No fim normal (ended) mantém-se o último frame do vídeo.
                  var fallback = () => this.classList.add('is-done');
                  this.video.addEventListener('error', fallback, { once: true });
                  var playPromise = this.video.play();
                  if (playPromise && typeof playPromise.catch === 'function') {
                    playPromise.catch(fallback);
                  }
                  this.io.unobserve(this);
                }
              });
            },
            { threshold: 0.2 }
          );
          this.io.observe(this);
        }

        disconnectedCallback() {
          if (this.io) this.io.disconnect();
        }
      }
    );
  }

  /* ── almeidas-tabs ───────────────────────────────────────────
     Tabs genéricos (usado em "Como preparar"): botões [data-tab-trigger]
     alimentam um painel único, lendo os data-* de cada botão. */
  if (!customElements.get('almeidas-tabs')) {
    customElements.define(
      'almeidas-tabs',
      class AlmeidasTabs extends HTMLElement {
        connectedCallback() {
          this.triggers = Array.from(this.querySelectorAll('[data-tab-trigger]'));
          if (!this.triggers.length) return;
          this.triggers.forEach((btn) => {
            btn.addEventListener('click', () => this.select(btn));
          });
        }

        select(btn) {
          this.triggers.forEach((b) => {
            b.classList.toggle('is-on', b === btn);
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
          });

          var img = this.querySelector('[data-tab-image]');
          if (img && btn.dataset.img) {
            img.src = btn.dataset.img;
            img.alt = btn.dataset.imgAlt || '';
          }

          var fields = ['grind', 'ratio', 'dose', 'time', 'temp', 'tip'];
          fields.forEach((field) => {
            var target = this.querySelector('[data-tab-' + field + ']');
            if (target && btn.dataset[field] !== undefined) {
              target.textContent = btn.dataset[field];
            }
          });
        }
      }
    );
  }

  /* ── almeidas-gallery ────────────────────────────────────────
     Galeria de fotos do produto: thumbnails [data-thumb] trocam a
     imagem em destaque [data-stage-image]. */
  if (!customElements.get('almeidas-gallery')) {
    customElements.define(
      'almeidas-gallery',
      class AlmeidasGallery extends HTMLElement {
        connectedCallback() {
          this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
          this.stageImg = this.querySelector('[data-stage-image]');
          if (!this.thumbs.length || !this.stageImg) return;

          this.thumbs.forEach((btn) => {
            btn.addEventListener('click', () => this.select(btn));
          });
        }

        select(btn) {
          this.thumbs.forEach((b) => {
            b.classList.toggle('is-on', b === btn);
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
          });
          if (btn.dataset.src) {
            this.stageImg.src = btn.dataset.src;
            this.stageImg.alt = btn.dataset.alt || '';
          }
        }
      }
    );
  }

  /* ── almeidas-sticky-cta ─────────────────────────────────────
     Barra fixa que aparece depois de scrollY > 600. */
  if (!customElements.get('almeidas-sticky-cta')) {
    customElements.define(
      'almeidas-sticky-cta',
      class AlmeidasStickyCta extends HTMLElement {
        connectedCallback() {
          this.threshold = 600;
          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }

        onScroll() {
          this.classList.toggle('is-visible', window.scrollY > this.threshold);
        }
      }
    );
  }

  /* ── almeidas-subscription ───────────────────────────────────
     Builder de subscrição: slider + presets de cadência. Cada preset
     ([data-preset]) traz consigo os valores já calculados em Liquid
     (preço, poupança, rótulo) para não duplicar formatação de moeda
     em JS — o slider apenas escolhe o preset cujo intervalo de dias
     contém o valor atual. */
  if (!customElements.get('almeidas-subscription')) {
    customElements.define(
      'almeidas-subscription',
      class AlmeidasSubscription extends HTMLElement {
        connectedCallback() {
          this.slider = this.querySelector('.subs-slider');
          this.presets = Array.from(this.querySelectorAll('[data-preset]'));
          if (!this.slider || !this.presets.length) return;

          this.marks = Array.from(this.querySelectorAll('.subs-mark'));

          this.marks.forEach((mark) => {
            mark.addEventListener('click', () => {
              var days = parseInt(mark.dataset.days, 10);
              this.slider.value = days;
              this.update(days);
            });
          });

          this.slider.addEventListener('input', () => {
            this.update(parseInt(this.slider.value, 10));
          });

          this.updateFill();
          this.update(parseInt(this.slider.value, 10));
        }

        presetFor(days) {
          var match = null;
          this.presets.forEach((p) => {
            var min = parseInt(p.dataset.min, 10);
            var max = parseInt(p.dataset.max, 10);
            if (days >= min && days <= max) match = p;
          });
          return match || this.presets[this.presets.length - 1];
        }

        updateFill() {
          var min = parseInt(this.slider.min, 10);
          var max = parseInt(this.slider.max, 10);
          var value = parseInt(this.slider.value, 10);
          var pct = ((value - min) / (max - min)) * 100;
          this.slider.style.setProperty('--subs-fill', pct + '%');
        }

        update(days) {
          var preset = this.presetFor(days);
          if (!preset) return;

          this.updateFill();

          this.marks.forEach((mark) => {
            var on = parseInt(mark.dataset.days, 10) === parseInt(preset.dataset.days, 10);
            mark.classList.toggle('is-on', on);
            mark.setAttribute('aria-pressed', on ? 'true' : 'false');
          });

          var flash = (el) => {
            if (!el) return;
            el.classList.add('is-updating');
            window.setTimeout(() => el.classList.remove('is-updating'), 150);
          };

          var setText = (selector, value) => {
            var el = this.querySelector(selector);
            if (el && value !== undefined) {
              flash(el);
              el.textContent = value;
            }
          };

          setText('[data-subs-freq]', preset.dataset.freq);
          setText('[data-subs-now]', preset.dataset.priceNow);
          setText('[data-subs-saved]', preset.dataset.saved);
          setText('[data-subs-meta-freq]', preset.dataset.freq);
          setText('[data-subs-submit-price]', preset.dataset.priceNow);

          var submitLink = this.querySelector('[data-subs-submit]');
          if (submitLink && preset.dataset.priceNow) {
            var base = submitLink.dataset.labelBase || 'Subscrever';
            submitLink.textContent = base + ' · ' + preset.dataset.priceNow;
          }

          // Ramo com selling plans reais: o cartão escolhido define o
          // plano submetido pelo form e o preço mostrado no botão.
          if (preset.dataset.planId) {
            var planInput = this.querySelector('input[name="selling_plan"]');
            if (planInput) planInput.value = preset.dataset.planId;
            var btnSub = this.querySelector('.almeidas-btn-sub');
            if (btnSub && preset.dataset.priceNow) btnSub.textContent = preset.dataset.priceNow;
          }
        }
      }
    );
  }

  /* ── Confirmação de adição ao carrinho ───────────────────────
     Subscreve o cartUpdate que o Dawn já publica (product-form.js,
     quick-add, cart-items) em vez de interceptar submits: assim
     qualquer botão da loja recebe a sequência. Encadeia o check no
     botão, o nudge do ícone e o pop do contador; o Dawn continua
     responsável por abrir a notificação. */
  function initAddToCartFeedback() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // O cartUpdate chega depois do fetch, quando o botão já perdeu o
    // .loading — guardar o botão premido no momento do clique é a única
    // forma fiável de saber de onde parte o voo.
    var lastClicked = null;
    document.addEventListener(
      'click',
      function (evt) {
        var btn = evt.target && evt.target.closest ? evt.target.closest('button[name="add"]') : null;
        if (btn) lastClicked = btn;
      },
      true
    );

    function buttonFor(source) {
      if (source === 'cart-items') return null;
      return (
        document.querySelector('.almeidas-btn.loading') ||
        lastClicked ||
        (document.activeElement && document.activeElement.closest
          ? document.activeElement.closest('.almeidas-btn')
          : null)
      );
    }

    function flashButton(btn) {
      if (!btn) return;
      btn.classList.add('is-added');
      window.setTimeout(function () { btn.classList.remove('is-added'); }, 1400);
    }

    /* Voo do produto: um clone da imagem (ou um disco dourado, quando o
       botão não tem imagem por perto) viaja do botão até ao ícone do
       carrinho. Vive em position:fixed sobre a página, por isso não
       interfere com o layout nem com o scroll. */
    function flyToCart(btn) {
      if (reduced || !btn) return;

      var target = document.getElementById('cart-icon-bubble');
      if (!target) return;

      var scope = btn.closest('.almeidas-hero, .almeidas-showcase, .almeidas-subs, section') || document;
      var img = scope.querySelector('img');

      var from = btn.getBoundingClientRect();
      var to = target.getBoundingClientRect();

      var flyer = document.createElement('div');
      flyer.className = 'almeidas-flyer';
      if (img && img.currentSrc) {
        flyer.style.backgroundImage = 'url("' + img.currentSrc + '")';
      } else {
        flyer.classList.add('almeidas-flyer--dot');
      }

      var size = 56;
      flyer.style.width = size + 'px';
      flyer.style.height = size + 'px';
      flyer.style.left = from.left + from.width / 2 - size / 2 + 'px';
      flyer.style.top = from.top + from.height / 2 - size / 2 + 'px';
      document.body.appendChild(flyer);

      var dx = to.left + to.width / 2 - (from.left + from.width / 2);
      var dy = to.top + to.height / 2 - (from.top + from.height / 2);

      var anim = flyer.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
          // sobe antes de cair no carrinho, para o percurso ler como um arco
          { transform: 'translate(' + dx * 0.5 + 'px, ' + (dy * 0.4 - 70) + 'px) scale(0.8)', opacity: 1, offset: 0.55 },
          { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.18)', opacity: 0.2, offset: 1 },
        ],
        { duration: 750, easing: 'cubic-bezier(.4, .05, .35, 1)', fill: 'forwards' }
      );

      var done = function () { if (flyer.parentNode) flyer.parentNode.removeChild(flyer); };
      anim.addEventListener('finish', done);
      window.setTimeout(done, 1200);
    }

    function nudgeCart(delay) {
      var wait = delay || 0;

      window.setTimeout(function () {
        var icon = document.getElementById('cart-icon-bubble');
        if (!icon || reduced) return;
        icon.classList.remove('is-receiving');
        // reflow para poder repetir a animação em adições seguidas
        void icon.offsetWidth;
        icon.classList.add('is-receiving');
        window.setTimeout(function () { icon.classList.remove('is-receiving'); }, 600);
      }, wait);

      if (reduced) return;

      // O bubble é re-renderizado pela Section Rendering API — o próprio
      // #cart-icon-bubble é substituído — e na primeira adição nem existe.
      // Observa um ancestral estável e anima o primeiro bubble que surja.
      // Marca o nó durante a animação para não a disparar duas vezes
      // (o observer e a verificação directa podem apanhar o mesmo bubble);
      // a marca sai no fim para a próxima adição voltar a animar.
      var bump = function (bubble) {
        if (!bubble || bubble.dataset.almeidasBumped === '1') return;
        bubble.dataset.almeidasBumped = '1';
        bubble.classList.add('is-bumped');
        window.setTimeout(function () {
          bubble.classList.remove('is-bumped');
          delete bubble.dataset.almeidasBumped;
        }, 500);
      };

      // O bubble pode surgir antes ou depois deste ponto, consoante a
      // Section Rendering API acabe primeiro. Em vez de depender dessa
      // corrida, sonda por um curto período até o encontrar.
      var elapsed = 0;
      var poll = window.setInterval(function () {
        var bubble = document.querySelector('.cart-count-bubble');
        elapsed += 60;
        if (bubble) {
          window.clearInterval(poll);
          // Estala quando o voo aterra, não quando parte.
          if (elapsed >= wait) bump(bubble);
          else window.setTimeout(function () { bump(bubble); }, wait - elapsed);
        } else if (elapsed >= 2000) {
          window.clearInterval(poll);
        }
      }, 60);
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var btn = buttonFor(event && event.source);
      flashButton(btn);
      flyToCart(btn);
      // O carrinho só reage quando o voo aterra (~600ms de 750ms).
      nudgeCart(btn && !reduced ? 600 : 0);
      lastClicked = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAddToCartFeedback);
  } else {
    initAddToCartFeedback();
  }
})();
